import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PaymentService } from '../../../services/payment.service';
import { PaymentLookup, PayAuthUser, PaymentResult } from '../../../models/payment.model';

type Step = 1 | 2 | 3 | 4 | 5;

const METHOD_LABELS: Record<string, string> = {
  cash:    'pay_m_cash',
  check:   'pay_m_check',
  tresor:  'pay_m_tresor',
  // Valeurs historiques : les marques restent littérales, le reste passe par i18n
  bankily: 'Bankily',
  masrivi: 'Masrivi',
  sedad:   'Sedad',
  other:   'pay_m_other',
};

@Component({
  selector: 'app-paiement',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, TranslateModule],
  templateUrl: './paiement.html',
  styleUrl: './paiement.css',
})
export class PaiementComponent implements OnInit {
  step: Step = 1;

  // Step 1 — reference lookup
  reference   = '';
  lookupLoading = false;
  lookupError   = '';

  // Step 2 — details
  lookup: PaymentLookup | null = null;

  // Step 3 — auth
  authUser: PayAuthUser | null = null;
  authMode: 'login' | 'register' = 'login';

  loginEmail    = '';
  loginPassword = '';
  loginLoading  = false;
  loginError    = '';
  showPassword  = false;

  regName     = '';
  regEmail    = '';
  regPassword = '';
  regConfirm  = '';
  regLoading  = false;
  regError    = '';
  showRegPwd  = false;

  // Step 4 — payment form
  payMethod    = '';
  payPhone     = '';
  payRef       = '';
  payLoading   = false;
  payError     = '';

  readonly methods = [
    { value: 'cash',   label: 'pay_m_cash',   icon: 'fa-solid fa-money-bill-wave' },
    { value: 'check',  label: 'pay_m_check',  icon: 'fa-solid fa-money-check' },
    { value: 'tresor', label: 'pay_m_tresor', icon: 'fa-solid fa-landmark' },
  ];

  // Step 5 — result
  result: PaymentResult | null = null;

  constructor(
    private paySvc: PaymentService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.authUser = this.paySvc.getUser();
  }

  // ── Step 1 ────────────────────────────────────────────────────────────────

  search(): void {
    const ref = this.reference.trim().toUpperCase();
    if (!ref) return;
    this.lookupLoading = true;
    this.lookupError   = '';
    this.lookup        = null;

    this.paySvc.lookup(ref).subscribe({
      next: data => {
        this.lookupLoading = false;
        this.lookup        = data;
        this.step          = 2;
      },
      error: err => {
        this.lookupLoading = false;
        this.lookupError   = err?.status === 404
          ? 'Référence introuvable. Vérifiez le numéro et réessayez.'
          : (err?.error?.message ?? 'Erreur réseau. Réessayez.');
      },
    });
  }

  // ── Step 2 ────────────────────────────────────────────────────────────────

  proceedToAuth(): void {
    this.step = this.paySvc.isAuthenticated() ? 4 : 3;
  }

  // ── Step 3 — login ────────────────────────────────────────────────────────

  login(): void {
    if (!this.loginEmail || !this.loginPassword) return;
    this.loginLoading = true;
    this.loginError   = '';

    this.paySvc.login(this.loginEmail, this.loginPassword).subscribe({
      next: user => {
        this.loginLoading = false;
        this.authUser     = user;
        this.step         = 4;
      },
      error: err => {
        this.loginLoading = false;
        this.loginError   = err?.status === 401 || err?.status === 422
          ? 'Email ou mot de passe incorrect.'
          : (err?.error?.message ?? 'Erreur réseau. Réessayez.');
      },
    });
  }

  switchAuthMode(mode: 'login' | 'register'): void {
    this.authMode   = mode;
    this.loginError = '';
    this.regError   = '';
  }

  register(): void {
    this.regError = '';
    if (!this.regName.trim())  { this.regError = 'Le nom est requis.'; return; }
    if (!this.regEmail.trim()) { this.regError = 'L\'email est requis.'; return; }
    if (this.regPassword.length < 6) { this.regError = 'Le mot de passe doit contenir au moins 6 caractères.'; return; }
    if (this.regPassword !== this.regConfirm) { this.regError = 'Les mots de passe ne correspondent pas.'; return; }

    this.regLoading = true;
    this.paySvc.register(this.regName.trim(), this.regEmail.trim(), this.regPassword).subscribe({
      next: user => {
        this.regLoading = false;
        this.authUser   = user;
        this.step       = 4;
      },
      error: err => {
        this.regLoading = false;
        const errors = err?.error?.errors;
        if (errors?.email)    { this.regError = errors.email[0]; return; }
        if (errors?.password) { this.regError = errors.password[0]; return; }
        this.regError = err?.error?.message ?? 'Erreur lors de l\'inscription. Réessayez.';
      },
    });
  }

  logout(): void {
    this.paySvc.logout();
    this.authUser = null;
    this.step     = 3;
  }

  // ── Step 4 — pay ──────────────────────────────────────────────────────────

  confirmPayment(): void {
    if (!this.payMethod || !this.lookup) return;
    this.payLoading = true;
    this.payError   = '';

    this.paySvc.pay({
      type:              this.lookup.type,
      entity_id:         this.lookup.entity_id,
      payment_method:    this.payMethod,
      phone_number:      this.payPhone   || undefined,
      payment_reference: this.payRef     || undefined,
    }).subscribe({
      next: res => {
        this.payLoading = false;
        this.result     = res;
        this.step       = 5;
      },
      error: err => {
        this.payLoading = false;
        this.payError   = err?.error?.message ?? 'Erreur lors du paiement. Réessayez.';
      },
    });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  reset(): void {
    this.step          = 1;
    this.reference     = '';
    this.lookup        = null;
    this.lookupError   = '';
    this.authMode      = 'login';
    this.loginEmail    = '';
    this.loginPassword = '';
    this.loginError    = '';
    this.regName       = '';
    this.regEmail      = '';
    this.regPassword   = '';
    this.regConfirm    = '';
    this.regError      = '';
    this.payMethod     = '';
    this.payPhone      = '';
    this.payRef        = '';
    this.payError      = '';
    this.result        = null;
  }

  formatAmount(n: number): string {
    return new Intl.NumberFormat('fr-FR').format(n) + ' MRU';
  }

  methodLabel(value: string): string {
    return METHOD_LABELS[value] ?? value;
  }

  statusClass(status: string): string {
    const map: Record<string, string> = {
      payment_pending:  'pay-status pay-status--warn',
      payment_partial:  'pay-status pay-status--info',
      approved:         'pay-status pay-status--ok',
      pending:          'pay-status pay-status--warn',
    };
    return map[status] ?? 'pay-status pay-status--gray';
  }

  typeIcon(): string {
    return this.lookup?.type === 'infraction'
      ? 'fa-solid fa-triangle-exclamation'
      : 'fa-solid fa-file-lines';
  }
}
